/* eslint-disable @typescript-eslint/no-explicit-any */
import { EditableComponent, ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { AEMReactCompMap, CardVertical } from '@marriott/mi-ui-library';
import clsx from 'clsx';
import { FC, Suspense, useEffect, useRef, useMemo, useState } from 'react';
import { CobrandVanilaCarousel as CardCarouselContainerMolecule } from '../../index';
import { CardCarouselProps, CarouselContainerProps } from './CobrandCarousel.types';
import { StyledCobrandCarousel } from './CobrandCarousel.styles';
import { isMobile, setObserver } from '../../modules/utils';
import { RESOLVED_URL } from '../../modules/constants';
import { CardOverview } from '../CardOverview';
import { useStore } from '../../modules/store/cobrandStore';

export const CobrandCarouselConfig = {
  emptyLabel: 'CobrandCarousel',
  isEmpty: () => true,
  resourceType: `mi-aem-common-spa/components/content/cardcarouselcontainer`,
};

export const CobrandCarouselComp: FC<CardCarouselProps> = (props: CardCarouselProps) => {
  const {
    headerText,
    subHeaderText,
    ctaLabel,
    ctaLink,
    eyebrow,
    ctaType,
    cardCount,
    openInaNewTab,
    totalNumberOfCards,
    styleclass,
    cqItems,
    componentId,
    pagePath,
    itemPath,
    isAuthorMode,
    variation,
    enableTabletBreakpoint = true,
    isCardComparisonFeatureEnabled,
    openInNewTab,
    trackingProperties,
    isRtl,
    resolvedUrl,
    enable2CardLayout,
    layout,
    cardTag,
  } = props;

  const [isMobileVariant, setIsMobileVariant] = useState(false);

  useEffect(() => {
    setIsMobileVariant(isMobile());
  }, []);

  const { cobrandData, setCobrandData } = useStore(store => store);
  const isCobrandPage = resolvedUrl?.includes(RESOLVED_URL.CREDITCARDURL);
  let { noOfCards, noOfCardsTablet } = props;
  const cobrandCarouselRef = useRef<HTMLDivElement>(null);
  const selectedCards: string[] = useMemo(() => [], []);
  const isCombo = (): boolean => {
    return variation === 'combo';
  };
  if (isCombo()) {
    noOfCards = 2;
    noOfCardsTablet = 2;
  }

  const mapper = AEMReactCompMap(props?.allowedComponents);

  useEffect(() => {
    setObserver(componentId, cobrandCarouselRef);
  }, [componentId]);
  useEffect(() => {
    setCobrandData({ ...cobrandData, allCarouselCards: selectedCards });
  }, [selectedCards]);

  // This function is used to render the component in authoring mode authorCardWrapper
  const authorCardWrapper = (index: number, innerCompStyleClass: string | undefined) => {
    return (
      <ResponsiveGrid
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        pagePath={pagePath}
        itemPath={`${itemPath}/${totalNumberOfCards[index]}`}
        columnCount="12"
        gridClassNames={''}
        customClassName={clsx(innerCompStyleClass)}
        config={{
          isEmpty: () => true,
          resourceType: 'mi-aem-common-spa/components/container',
        }}
      />
    );
  };

  // This function is used to render the component in end-user mode

  const PublishCardsWrapper = useMemo(() => {
    const publishCardWrapper = (cardName: string, jsonData: any) => {
      // eslint-disable-next-line no-prototype-builtins
      if (jsonData?.hasOwnProperty(cardName)) {
        const card = jsonData[cardName];
        const cardItems = card[':items'];
        for (const itemKey in cardItems) {
          if (Object.prototype.hasOwnProperty.call(cardItems, itemKey)) {
            let item = cardItems[itemKey];
            if (itemKey.includes('cardvertical')) {
              return (
                <li key={itemKey}>
                  <CardVertical {...item} cardVerticalVariations="standard" />;
                </li>
              );
            } else if (itemKey.includes('cardoverview') && noOfCards) {
              item['customClass'] = enable2CardLayout
                ? 'card-overview-featured-cards'
                : noOfCards === 2
                ? 'card-overview-two-cards'
                : noOfCards === 3
                ? 'card-overview-three-cards'
                : '';
            }
            const itemType = item[':type']?.split('/').pop();
            if (Object.prototype.hasOwnProperty.call(mapper, itemType)) {
              const innerComp = mapper[itemType];
              if (innerComp === 'CardOverview') {
                if (enable2CardLayout) {
                  selectedCards.push(item.cardCode);
                }
                item = {
                  ...item,
                  isCardComparisonFeatureEnabled: isCardComparisonFeatureEnabled,
                  enable2CardLayout: enable2CardLayout,
                  layout: layout,
                };
              }
              return (
                <li className="container px-0 d-flex" key={itemKey}>
                  <Suspense fallback={<div></div>}>
                    <CardOverview {...item} />
                  </Suspense>
                </li>
              );
            }
            return null;
          }
        }
      }
      return null;
    };
    return totalNumberOfCards?.map((cardName: string) => publishCardWrapper(cardName, cqItems));
  }, [totalNumberOfCards, cqItems, isCardComparisonFeatureEnabled, mapper]);

  return (
    <StyledCobrandCarousel
      data-testid="cardcarouselcontainer"
      data-component-name="o-cobrand-cardcarouselcontainer"
      ref={cobrandCarouselRef}
    >
      <div>
        <CardCarouselContainerMolecule
          componentId={componentId}
          subHeaderText={subHeaderText}
          styleclass={styleclass}
          variation={variation}
          eyebrow={eyebrow}
          cardCount={cardCount}
          trackingProperties={trackingProperties}
          ctaLabel={ctaLabel}
          headerText={headerText}
          ctaLink={ctaLink}
          openInaNewTab={openInaNewTab}
          ctaType={ctaType}
          isCombo={isCombo}
          noOfCards={noOfCards}
          noOfCardsTablet={noOfCardsTablet}
          enableTabletBreakpoint={enableTabletBreakpoint}
          isDirectionRightToLeft={isRtl}
          isCobrandVariant={isCobrandPage && isMobileVariant}
          layout={layout}
          cardTag={cardTag}
        >
          {isAuthorMode &&
            Array.from({ length: totalNumberOfCards?.length }, (_, i) => authorCardWrapper(i, styleclass))}
          {!isAuthorMode && PublishCardsWrapper}
        </CardCarouselContainerMolecule>
      </div>
    </StyledCobrandCarousel>
  );
};

export const CarouselEditableComponent = (props: any) => {
  return (
    <EditableComponent config={CobrandCarouselConfig} {...props}>
      <CobrandCarouselComp {...props} componentName={props?.model.cqType.split('/').pop()} />
    </EditableComponent>
  );
};

export const CobrandCarousel = (props: CarouselContainerProps) => {
  const { model } = props;
  if (model.variation === 'combo' && !model.styleclass?.includes('fullbleed')) {
    model.styleclass += ' fullbleed';
  }
  return (
    <div
      className={clsx(
        [model.styleclass?.includes('inverse') ? 'inverse' : ''],
        [model.styleclass?.includes('fullbleed') ? 'm-container-fullbleed' : ''],
        [model.styleclass?.includes('alternate') ? 'alternate' : '']
      )}
      data-testid="card-carousel"
    >
      <div className={clsx('container', { 'p-0': !model.styleclass?.includes('fullbleed') })}>
        <CarouselEditableComponent {...props} />
      </div>
    </div>
  );
};
